import type { Avatar, AvatarFace, AvatarShape } from '@/api/types';

export const AVATAR_SHAPES: AvatarShape[] = ['blob', 'star', 'circle', 'flower', 'bolt'];
export const AVATAR_FACES: AvatarFace[] = ['smile', 'grin', 'wink', 'cool', 'chill'];
export const INK = '#1A1A1A';

export function clampHue(h: number): number {
  if (!Number.isFinite(h)) return 0;
  const r = Math.round(h) % 360;
  return r < 0 ? r + 360 : r;
}

/**
 * Blobs are cut-paper stickers in the poster colours (docs/DESIGN.md): the
 * person's hue snaps to the nearest one, so a pick still feels like theirs.
 */
const POSTER_FILLS: [maxHue: number, fill: string][] = [
  [18, '#ED4B44'], // red
  [40, '#F47B2A'], // orange
  [68, '#F2C21A'], // yellow
  [165, '#18BC42'], // green
  [255, '#2F6BFF'], // blue
  [335, '#F28DB2'], // pink
  [360, '#ED4B44'],
];
export function avatarFill(hue: number): string {
  const h = clampHue(hue);
  return POSTER_FILLS.find(([max]) => h < max)?.[1] ?? '#ED4B44';
}

const f = (n: number) => Number(n.toFixed(2));

function circlePath(cx: number, cy: number, r: number): string {
  return `M${f(cx - r)} ${f(cy)}a${f(r)} ${f(r)} 0 1 0 ${f(2 * r)} 0a${f(r)} ${f(r)} 0 1 0 ${f(-2 * r)} 0Z`;
}

function starPath(points: number, outer: number, inner: number, cx = 50, cy = 50, rot = -Math.PI / 2): string {
  const pts: string[] = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = rot + (i * Math.PI) / points;
    pts.push(`${f(cx + r * Math.cos(a))} ${f(cy + r * Math.sin(a))}`);
  }
  return `M${pts.join('L')}Z`;
}

/** Shape outlines in a 100×100 box. Star/bolt also get a same-colour round stroke to soften corners. */
export const SHAPE_PATHS: Record<AvatarShape, string> = {
  circle: circlePath(50, 50, 44),
  blob: 'M52 6c17 0 33 9 39 25 6 15 2 32-8 44-10 12-26 20-42 18-15-2-29-12-34-27C2 51 6 33 16 21 25 11 38 6 52 6Z',
  star: starPath(9, 46, 36),
  flower: [
    ...Array.from({ length: 6 }, (_, i) => {
      const a = (i * Math.PI) / 3 - Math.PI / 2;
      return circlePath(50 + 26 * Math.cos(a), 50 + 26 * Math.sin(a), 20);
    }),
    circlePath(50, 50, 30),
  ].join(''),
  bolt: 'M58 4 16 56h28l-8 40 48-56H56l10-36Z',
};

/** Where the face sits for each shape (centre + scale). */
const FACE_ANCHOR: Record<AvatarShape, { x: number; y: number; s: number }> = {
  circle: { x: 50, y: 52, s: 1 },
  blob: { x: 50, y: 52, s: 1 },
  star: { x: 50, y: 52, s: 0.95 },
  flower: { x: 50, y: 52, s: 0.95 },
  bolt: { x: 50, y: 47, s: 0.7 },
};

export type FacePart =
  | { kind: 'circle'; cx: number; cy: number; r: number }
  | { kind: 'path'; d: string; fill?: boolean; width?: number };

/** Face parts drawn relative to (0,0), scaled and placed by the renderer. */
export function faceParts(face: AvatarFace): FacePart[] {
  switch (face) {
    case 'smile':
      return [
        { kind: 'circle', cx: -11, cy: -6, r: 4 },
        { kind: 'circle', cx: 11, cy: -6, r: 4 },
        { kind: 'path', d: 'M-11 7q11 10 22 0', width: 4.5 },
      ];
    case 'grin':
      return [
        { kind: 'circle', cx: -11, cy: -8, r: 4 },
        { kind: 'circle', cx: 11, cy: -8, r: 4 },
        { kind: 'path', d: 'M-14 3h28q-2 15-14 15T-14 3Z', fill: true },
      ];
    case 'wink':
      return [
        { kind: 'path', d: 'M-16 -6q5 -6 10 0', width: 4 },
        { kind: 'circle', cx: 11, cy: -6, r: 4 },
        { kind: 'path', d: 'M-10 7q12 10 20 -2', width: 4.5 },
      ];
    case 'cool':
      return [
        { kind: 'path', d: 'M-22 -12h44v4q0 9-9 9h-6q-6 0-7-6h0q-1 6-7 6h-6q-9 0-9-9Z', fill: true },
        { kind: 'path', d: 'M-8 10q8 4 16 0', width: 4 },
      ];
    case 'chill':
    default:
      return [
        { kind: 'path', d: 'M-16 -5q5 4 10 0', width: 4 },
        { kind: 'path', d: 'M6 -5q5 4 10 0', width: 4 },
        { kind: 'path', d: 'M-7 9h14', width: 4.5 },
      ];
  }
}

export interface AvatarSpec {
  fill: string;
  shapePath: string;
  softenStroke: number; // 0 = none
  face: FacePart[];
  transform: string;
}

export function normalizeAvatar(a: Partial<Avatar> | null | undefined): Avatar {
  return {
    hue: clampHue(a?.hue ?? 12),
    shape: a?.shape && AVATAR_SHAPES.includes(a.shape) ? a.shape : 'circle',
    face: a?.face && AVATAR_FACES.includes(a.face) ? a.face : 'smile',
  };
}

export function buildAvatar(input: Partial<Avatar> | null | undefined): AvatarSpec {
  const a = normalizeAvatar(input);
  const anchor = FACE_ANCHOR[a.shape];
  return {
    fill: avatarFill(a.hue),
    shapePath: SHAPE_PATHS[a.shape],
    softenStroke: a.shape === 'star' || a.shape === 'bolt' ? 6 : 0,
    face: faceParts(a.face),
    transform: `translate(${anchor.x} ${anchor.y}) scale(${anchor.s})`,
  };
}

/** Standalone SVG markup (export, favicons, tests). */
export function avatarSvg(input: Partial<Avatar> | null | undefined, size = 64): string {
  const s = buildAvatar(input);
  const stroke = s.softenStroke ? ` stroke="${s.fill}" stroke-width="${s.softenStroke}" stroke-linejoin="round"` : '';
  const parts = s.face
    .map((p) =>
      p.kind === 'circle'
        ? `<circle cx="${p.cx}" cy="${p.cy}" r="${p.r}" fill="${INK}"/>`
        : p.fill
          ? `<path d="${p.d}" fill="${INK}"/>`
          : `<path d="${p.d}" fill="none" stroke="${INK}" stroke-width="${p.width ?? 4}" stroke-linecap="round" stroke-linejoin="round"/>`,
    )
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${size}" height="${size}"><path d="${s.shapePath}" fill="${s.fill}"${stroke}/><g transform="${s.transform}">${parts}</g></svg>`;
}

/** Deterministic avatar from any string (fallback when a user has none, e.g. onboarding default). */
export function avatarFromSeed(seed: string): Avatar {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const u = h >>> 0;
  return {
    hue: u % 360,
    shape: AVATAR_SHAPES[(u >>> 9) % AVATAR_SHAPES.length]!,
    face: AVATAR_FACES[(u >>> 13) % AVATAR_FACES.length]!,
  };
}
